/**
 * Блок сводного остатка платёжных систем (карточка BL-2026-09-29-beba).
 *
 * Flow: опрос /api/treasury/psp-balances раз в 60 секунд → одна ручка даёт
 *       и долларовый итог, и свод по валютам → величина сверху, перечень
 *       активов под ней.
 *
 * Зависимости:
 *   - react: состояние и жизненный цикл;
 *   - api.js: loadPspBalances.
 *
 * Связанные файлы:
 *   - src/App.jsx — размещает блок в разделе PSP/Bank;
 *   - src/CryptoValuationPanel.jsx — оценка суточного среза; её величина
 *     ОТЛИЧАЕТСЯ от здешней по построению, см. ниже.
 *
 * # ИТОГ ОБЯЗАН БЫТЬ СУММОЙ ТОГО, ЧТО ПОД НИМ НАПИСАНО
 *
 * Первая редакция брала долларовую величину у блока оценки криптоактивов.
 * Это была ошибка, и заметил её заказчик: оценка считает СУТОЧНЫЙ СРЕЗ
 * на конец дня, а перечень активов показывает ЖИВЫЕ остатки. 2026-09-29
 * они разошлись на 38 760 USDT — пришедших уже после среза, — и над
 * списком стояло число, которое из этого списка не выводится.
 *
 * Теперь итог приходит той же ручкой, что и активы, и посчитан по тем же
 * живым остаткам (см. PSPBalancesReport.TotalUSD). Оценка на конец суток
 * никуда не делась — она в своём блоке и отвечает на свой вопрос:
 * сопоставимость между днями и сверка с ручной строкой бэкофиса.
 */

import { useCallback, useEffect, useState } from 'react'

import { loadPspBalances } from './api'

/** Период опроса — как у остальных блоков платёжного контура. */
const POLL_MS = 60000

/**
 * Показывает величину в долларах.
 *
 * @param {string|null} value — сумма строкой
 * @returns {string} сумма с двумя знаками либо прочерк
 *
 * Пустое значение — прочерк, а не ноль: «оценки нет» и «оценка ноль» —
 * разные утверждения (BR-011).
 */
export function formatUsdTotal(value) {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return value
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/**
 * Показывает остаток актива с двумя знаками после запятой.
 *
 * @param {string|null} value — остаток строкой
 * @returns {string} округлённая величина, «< 0,01» либо прочерк
 *
 * Блок сводный, и восемь знаков крипты в нём — шум: точную величину
 * показывает блок «Остатки платёжных систем» ниже, где на неё и смотрят
 * при разборе.
 *
 * НО НЕНУЛЕВОЙ ОСТАТОК НИКОГДА НЕ ПОКАЗЫВАЕТСЯ НУЛЁМ. Округление до двух
 * знаков превратило бы 0,004 ETH в «0,00» — то есть в утверждение «денег
 * нет» о деньгах, которые есть (BR-011, тот же довод, по которому кошелёк
 * без снимка отдаётся прочерком, а не нулём). Такая величина показывается
 * как «< 0,01»: она мала, но она не ноль, и это видно.
 */
export function formatAssetAmount(value) {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return value
  if (n !== 0 && Math.abs(n) < 0.005) return '< 0,01'
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/**
 * Показывает момент курса днём и временем UTC.
 *
 * @param {string|undefined} iso — момент котировки
 * @returns {string} «29.09 06:22 UTC» либо пустая строка
 */
export function formatRateMoment(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const дд = String(d.getUTCDate()).padStart(2, '0')
  const мм = String(d.getUTCMonth() + 1).padStart(2, '0')
  const чч = String(d.getUTCHours()).padStart(2, '0')
  const мин = String(d.getUTCMinutes()).padStart(2, '0')
  return `${дд}.${мм} ${чч}:${мин} UTC`
}

export default function PspTotalsPanel() {
  const [balances, setBalances] = useState(null)
  const [error, setError] = useState('')

  const load = useCallback(() => {
    loadPspBalances()
      .then((d) => {
        setBalances(d)
        setError('')
      })
      .catch((e) => setError(e.message))
  }, [])

  useEffect(() => {
    load()
    const timer = setInterval(load, POLL_MS)
    return () => clearInterval(timer)
  }, [load])

  if (error) {
    return (
      <section className="card">
        <h2>Общий баланс PSP</h2>
        <div className="error">{error}</div>
      </section>
    )
  }

  if (!balances) {
    return (
      <section className="card">
        <h2>Общий баланс PSP</h2>
        <p className="muted">Загрузка…</p>
      </section>
    )
  }

  // Блок скрывается целиком, пока ни одна платёжная система не включена —
  // как и подробные блоки контура: пустая таблица там, где данных не может
  // быть по построению, учит не смотреть на блок.
  if (!balances.sources || balances.sources.length === 0) return null

  const assets = balances.assets || []

  return (
    <section className="card">
      <h2>Общий баланс PSP</h2>

      <p className="metric-big">
        {formatUsdTotal(balances.total_usd)} <span className="unit">USD</span>
      </p>
      {balances.rate_as_of ? (
        <p className="muted metric-note">
          Сумма активов ниже по последним курсам; самый старый из них снят{' '}
          {formatRateMoment(balances.rate_as_of)}.
        </p>
      ) : (
        // Курсов нет — говорим об этом словами, а не пустым местом:
        // прочерк без объяснения читается как поломка.
        <p className="muted metric-note">
          Курсы к доллару ещё не загружены — перевести остатки не в чем.
        </p>
      )}

      {/* Неполнота итога называется рядом с ним: сумма, под которой
          не сказано, что она неполная, читается как полная (BR-023). */}
      {balances.total_usd_complete === false && (
        <div className="incomplete">
          <strong className="warn">В долларовый итог вошли не все активы.</strong>
          <ul>
            {(balances.total_usd_reasons || []).map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      <h3>Активы</h3>
      {assets.length === 0 ? (
        <p className="muted">Остатков ещё нет.</p>
      ) : (
        // ДВА столбца, и это предел ширины, а не лень.
        //
        // Остаток с оценкой стоит ОДНОЙ строкой, и строке этой нужно 406
        // пикселей при колонке в 376 (замерено на боевом экране 1600).
        // Столбцы «Системы» и «Кошельков» убраны ради неё: оба отвечают
        // на вопросы, которые тут же ниже разбирает блок «Остатки платёжных
        // систем», а этот блок сводный — он отвечает «сколько и в чём».
        // Системы остались подсказкой при наведении на актив.
        //
        // Единственное, что из «Кошельков» НЕЛЬЗЯ было потерять, — признак
        // неполноты: свод по трём кошелькам из пяти не свод (BR-023).
        // Он переехал строкой под название актива и показывается только
        // когда он есть; молчание означает «вошли все».
        <table>
          <thead>
            <tr>
              <th>Актив</th>
              <th className="num">Остаток</th>
            </tr>
          </thead>
          <tbody>
            {assets.map((a) => (
              <tr key={a.currency_code}>
                <td title={`системы: ${(a.sources || []).join(', ')}`}>
                  {a.currency_code}
                  {a.missing > 0 && (
                    <div className="warn cell-note" title="кошельки без остатка в свод не вошли">
                      +{a.missing} кошелька без остатка
                    </div>
                  )}
                </td>
                <td className="num asset-amount">
                  {formatAssetAmount(a.total)}
                  {/* Оценка актива — в ОДНОЙ строке с остатком. Итог сверху
                      обязан сходиться с суммой этих оценок, и читающий должен
                      иметь возможность это проверить, не веря на слово. */}
                  {a.amount_usd ? (
                    <span className="muted cell-note" title={`курс ${a.rate}`}>
                      {' '}≈ {formatUsdTotal(a.amount_usd)} USD
                    </span>
                  ) : (
                    <span className="warn cell-note"> курса нет — в итог не вошёл</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
